import { Component, computed, NgZone, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { PhotoCaptureComponent } from '../../photo/photo-capture.component';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { VISITOR_TYPES, VisitorType } from '../../ipad-visitor-type/ipad-visitor-type.component';
import { CheckinBaseComponent } from '../checkin-base.component';
import { CheckinShellComponent } from '../checkin-shell/checkin-shell.component';
import { HealthyViewerComponent } from "../healthy/healthy-viewer.component";
import { QuizViewerComponent } from "../quiz/quiz-viewer.component";
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { PolicyViewerComponent } from "../policy/policy-viewer.component";

@Register(Visit)
@Component({
    selector: 'app-group-checkin',
    templateUrl: './group-checkin.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, TranslatePipe, OnReadyDirective, FieldWrapperComponent, PhotoCaptureComponent, MultiSelectComponent, CheckinShellComponent, HealthyViewerComponent, QuizViewerComponent, PolicyViewerComponent]
})
export class GroupCheckinComponent extends CheckinBaseComponent {

    visitorType: VisitorType | null = VISITOR_TYPES.find(vt => vt.key === 'group') ?? null;

    /** Employé visité, choisi une seule fois pour tout le groupe (écran 1). */
    private groupEmployeeId?: number;
    private groupEmployeePKs: string[] = [];

    /** Visite du 1er membre (initiateur), gardée en mémoire et envoyée en dernier
     *  pour que la notification groupée contienne le nom de tous les membres. */
    private pendingInitiatorVisit: Visit | null = null;

    /** Noms des membres enregistrés après l'initiateur. */
    groupMemberNames: string[] = [];

    /** Nombre de membres dont l'enregistrement est terminé (initiateur compris). */
    memberCount = 0;

    /** Libellé de la prochaine action (policy / covid / quiz / save) selon la config POS. */
    nextActionStepId = computed(() => {
        const entity = this.editingEntity();
        const current = this.currentStep();
        const visibleSteps = this.stepValidator.getVisibleSteps();
        const currentIdx = visibleSteps.findIndex(s => s.stepNumber === current);

        for (let i = currentIdx + 1; i < visibleSteps.length; i++) {
            const candidate = visibleSteps[i];
            if (entity && candidate.canAutoSkip(entity, this.currentPos)) {
                continue;
            }
            return candidate.stepId;
        }
        return 'save';
    });

    /** Clé de traduction du libellé de la prochaine action. */
    nextActionLabelKey = computed(() => {
        switch (this.nextActionStepId()) {
            case 'group-policy-signature': return 'vues.ipad.policies.headerTitle';
            case 'group-healthy': return 'vues.ipad.healthy.headerTitle';
            case 'group-quiz': return 'vues.ipad.quiz.headerTitle';
            case 'group-employee': return 'entities.visit.propreties.employee';
            case 'group-visitor': return 'vues.ipad.checkin.group.next-visitor';
            default: return 'vues.ipad.checkin.group.save-member';
        }
    });

    constructor(
        route: ActivatedRoute,
        router: Router,
        translationService: TranslationService,
        validationService: GenericValidationService,
        ngZone: NgZone,
        navService: NavigationService,
        tenantService: CurrentTenantService,
        alertModalService: AlertModalService
    ) {
        super(route, router, translationService, validationService, ngZone, navService, tenantService, alertModalService);
    }

    override getEmptyEntity(): Visit {
        const visit = super.getEmptyEntity();
        visit.visitorType = 1; // Visitor
        visit.isGroupedVisit = true;
        if (this.groupEmployeeId) {
            visit.fK_Employee = this.groupEmployeeId;
            visit.selectedPK_Employee = [...this.groupEmployeePKs];
        }
        return visit;
    }

    override onEmployeeSelectChange(evt: string[]) {
        super.onEmployeeSelectChange(evt);
        const visit = this.editingEntity();
        this.groupEmployeeId = visit?.fK_Employee;
        this.groupEmployeePKs = [...(visit?.selectedPK_Employee ?? [])];
    }

    override async onVisitorSelectChange(ids: string[]) {
        await super.onVisitorSelectChange(ids);

        // L'employé est choisi une seule fois pour tout le groupe :
        // on ré-applique le choix du groupe après le pré-remplissage
        // effectué à partir de la dernière visite du visiteur.
        const visit = this.editingEntity();
        if (visit && this.groupEmployeeId) {
            visit.fK_Employee = this.groupEmployeeId;
            visit.selectedPK_Employee = [...this.groupEmployeePKs];
            this.editingEntity.set(Object.assign(new Visit(), visit));
        }
    }

    // --- Flow du groupe ---

    /**
     * Termine l'enregistrement du membre courant puis revient à l'écran
     * de sélection du visiteur pour le membre suivant.
     */
    async completeMember(): Promise<void> {
        const visit = this.editingEntity();
        if (!visit) return;

        visit.isGroupedVisit = true;

        if (!this.pendingInitiatorVisit) {
            // Premier membre = initiateur : badge imprimé maintenant, mais la visite
            // n'est postée qu'à la fin du process (avec la liste des autres membres).
            visit.isGroupInitiator = true;
            await this.printVisitBadge(visit);
            this.pendingInitiatorVisit = visit;
        } else {
            // Membres suivants : postés immédiatement, sans notification d'arrivée
            // (le backend notifie uniquement pour IsGroupInitiator).
            await this.printVisitBadge(visit);
            const saved = await this.postCheckin(visit);
            if (!saved) return; // échec : le membre reste affiché pour réessayer
            this.groupMemberNames.push(visit.visitor?.fullname ?? '');
        }

        this.memberCount++;
        this.resetForNextMember();
    }

    /**
     * Envoie la visite de l'initiateur avec la liste des autres membres.
     * C'est cette visite qui déclenche la notification "groupe arrivé".
     */
    async finishGroup(): Promise<void> {
        const initiator = this.pendingInitiatorVisit;
        if (initiator) {
            initiator.groupVisitors = this.groupMemberNames.join(', ');
            const saved = await this.postCheckin(initiator);
            if (!saved) return;
            this.pendingInitiatorVisit = null;
        }
        this.goHome();
    }

    private resetForNextMember(): void {
        this.policyAccepted = false;
        this.pdfRead.set(false);

        const visit = this.getEmptyEntity();
        this.editingEntity.set(visit);
        this.badgeData = { ...this.badgeData, visit: visit };
        this.currentStep.set(2); // retour à l'écran visiteur
        this.focusFirstFieldOfCurrentStep();
    }

    override goForward() {
        const next = this.stepValidator.goForward();
        if (next === null) {
            this.completeMember();
            return;
        }
        this.focusFirstFieldOfCurrentStep();
    }

    override goBack() {
        const prev = this.stepValidator.goBack();
        if (prev === null) {
            // Sortie du wizard : finaliser le groupe si un initiateur est en attente
            if (this.pendingInitiatorVisit) {
                this.finishGroup();
            } else {
                this.navService.goBack();
            }
            return;
        }
        this.focusFirstFieldOfCurrentStep();
    }

    override goHome() {
        // Quitter en cours de groupe = terminer le groupe (sauvegarde l'initiateur)
        if (this.pendingInitiatorVisit) {
            this.finishGroup();
        } else {
            super.goHome();
        }
    }

}
